import { useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useStore } from '../lib/store'
import { PLANS, planById } from '../lib/constants'
import { fullDate, isValidDate, money } from '../lib/format'
import { canManageBilling } from '../lib/permissions'
import type { PlanId } from '../lib/types'
import { STRIPE_PUBLISHABLE_KEY } from '../lib/config'
import { Badge, Button, Card, Meter, Modal } from '../components/ui'
import { IconCheck } from '../components/icons'
import { GhostMark } from '../components/GhostMark'
import '../components/black.css'
import './pages.css'

// One Stripe.js instance for the whole screen. Null when no publishable key is
// configured (dev without Stripe) — the plan picker then shows a config notice
// instead of letting a recruiter start a checkout that can't complete.
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null

// What the card-entry modal is collecting: payment for a brand-new subscription,
// or a replacement card for an existing one ("Update card").
type CardFlow = { mode: 'subscribe' | 'setup'; clientSecret: string; planName?: string }

// The actual card form. Lives inside <Elements> so it can use the hooks; it
// confirms the PaymentIntent (subscribe) or SetupIntent (update card) the server
// handed us, then lets the Stripe webhook write the real state back.
function CardForm({ flow, onDone }: { flow: CardFlow; onDone: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const { finishCardUpdate } = useStore()
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setErr(null)
    try {
      const confirmParams = { return_url: window.location.href }
      if (flow.mode === 'subscribe') {
        const { error } = await stripe.confirmPayment({ elements, confirmParams, redirect: 'if_required' })
        if (error) throw new Error(error.message)
      } else {
        const { error, setupIntent } = await stripe.confirmSetup({ elements, confirmParams, redirect: 'if_required' })
        if (error) throw new Error(error.message)
        // Adopt the freshly-saved card as the default (server-side).
        if (typeof setupIntent?.payment_method === 'string') {
          await finishCardUpdate(setupIntent.payment_method)
        }
      }
      onDone()
    } catch (e2) {
      setErr((e2 as Error).message || 'Payment could not be completed.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="stack">
      <PaymentElement />
      {err && <div className="pay-error">{err}</div>}
      <Button type="submit" variant="primary" block loading={submitting} disabled={!stripe || submitting}>
        {flow.mode === 'subscribe' ? `Subscribe to ${flow.planName}` : 'Save card'}
      </Button>
    </form>
  )
}

export function Billing() {
  const { session, billing, allowanceUsed, allowanceTotal, subscribeToPlan, cancelPlan, resumePlan, beginCardUpdate } =
    useStore()
  // Only an owner/admin can change the plan or card; everyone else sees billing
  // read-only. The api sidecar enforces the same, so a disabled control matches
  // what the server would allow.
  const canManage = canManageBilling(session.recruiter.seatRole)
  const current = planById(billing.planId)
  // Free accounts have no billing renewal (`renewsOn` is empty) — guard so the UI
  // never renders "Renews Invalid Date" / "resets —".
  const renews = isValidDate(billing.renewsOn)
  const unlimited = !Number.isFinite(allowanceTotal)
  const overage = unlimited ? 0 : Math.max(0, allowanceUsed - allowanceTotal)
  const overageCost = current.overageCents ? overage * current.overageCents : 0
  const isBlack = billing.planId === 'black'
  const hasSubscription = !!billing.stripeSubscriptionId
  const scheduledDowngrade = !!billing.cancelAtPeriodEnd

  const [flow, setFlow] = useState<CardFlow | null>(null)
  const [pending, setPending] = useState<PlanId | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Start or switch a paid plan. If the org already has a subscription the
  // server re-prices it directly (no card step); otherwise it returns a
  // clientSecret and we open the card modal to collect payment.
  async function handleSwitch(planId: PlanId, planName: string) {
    setError(null)
    setPending(planId)
    try {
      const res = await subscribeToPlan(planId)
      if (res.status === 'requires_payment' && res.clientSecret) {
        setFlow({ mode: 'subscribe', clientSecret: res.clientSecret, planName })
      }
      // 'updated' (and confirmed payments) flow back through the realtime
      // billing subscription — nothing to set locally.
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setPending(null)
    }
  }

  // Downgrade to Free — scheduled for the end of the current billing period (no
  // refund); the plan stays active until then. Confirm first.
  async function handleCancel() {
    if (
      !window.confirm(
        `Downgrade to Free? You'll keep ${current.name} until ${fullDate(billing.renewsOn)}, then drop to Free. No refund for the rest of the period.`,
      )
    )
      return
    setError(null)
    setPending('free')
    try {
      await cancelPlan()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setPending(null)
    }
  }

  // Undo a scheduled downgrade — keep the current plan.
  async function handleResume() {
    setError(null)
    setPending(billing.planId)
    try {
      await resumePlan()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setPending(null)
    }
  }

  // "Update card" — fetch a SetupIntent and open the modal in setup mode.
  async function handleUpdateCard() {
    setError(null)
    try {
      const clientSecret = await beginCardUpdate()
      setFlow({ mode: 'setup', clientSecret })
    } catch (e) {
      setError((e as Error).message)
    }
  }

  // Vouchr Black is a comp tier: a single gold hero, no self-serve plan picker
  // (so it can't be accidentally downgraded), and no payment method / invoices.
  if (isBlack) {
    return (
      <div className="page">
        <div className="page__head">
          <div>
            <h1 className="page__title">Billing</h1>
            <p className="page__sub">Your plan and verification usage.</p>
          </div>
        </div>

        <div className="black-hero">
          <div className="black-hero__top">
            <GhostMark size={40} tint="founding" glow />
            <div className="black-hero__wordmark gold-foil">Vouchr Black</div>
            <Badge color="var(--cred-founding)" bg="var(--cred-founding-bg)" style={{ marginLeft: 'auto' }}>
              Complimentary
            </Badge>
          </div>
          <p className="black-hero__sub">{current.tagline}</p>
          <div className="black-hero__chips">
            {current.features.map((f) => (
              <span key={f} className="black-hero__chip">
                <IconCheck size={13} /> {f}
              </span>
            ))}
          </div>
          <div className="black-hero__note">
            <b>{allowanceUsed}</b> verification{allowanceUsed === 1 ? '' : 's'} this month · no cap. Your account is
            on the house — assigned and managed by Vouchr, so there’s nothing to pay and no invoices.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Billing</h1>
          <p className="page__sub">Your plan, verification allowance, and invoices.</p>
        </div>
      </div>

      <div className="stack">
        {error && <div className="pay-error">{error}</div>}

        {/* Current usage */}
        <Card>
          <div className="row-between" style={{ marginBottom: '1rem', alignItems: 'flex-start' }}>
            <div>
              <div className="section-title" style={{ margin: 0 }}>Current plan</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.02em', marginTop: '0.3rem' }}>
                {current.name}{' '}
                <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--c-dim)' }}>
                  {current.priceLabel}
                  {current.price ? '/mo' : ''}
                </span>
              </div>
            </div>
            {scheduledDowngrade ? (
              <Badge color="var(--c-warn)" bg="var(--c-warn-bg)">
                Downgrades {fullDate(billing.renewsOn)}
              </Badge>
            ) : renews ? (
              <Badge color="var(--cred-live)" bg="var(--cred-live-bg)" dot live>
                Renews {fullDate(billing.renewsOn)}
              </Badge>
            ) : (
              <Badge color="var(--c-faint)" bg="var(--surface-2)">
                {current.name} plan
              </Badge>
            )}
          </div>
          <Meter
            label="Verifications this month"
            used={allowanceUsed}
            total={unlimited ? allowanceUsed : allowanceTotal}
            unit={unlimited ? 'unlimited' : 'used'}
            foot={
              unlimited
                ? `${current.name} plan — unlimited verifications${renews ? ` · resets ${fullDate(billing.renewsOn)}` : ''}`
                : overage > 0
                  ? `${overage} over allowance · ${money(overageCost)} in overage at ${money(current.overageCents ?? 0)} each`
                  : `${allowanceTotal - allowanceUsed} remaining${renews ? ` · resets ${fullDate(billing.renewsOn)}` : ''}`
            }
          />
          {scheduledDowngrade && (
            <div className="row-between" style={{ marginTop: '1rem', gap: '1rem', alignItems: 'center' }}>
              <div className="setting-row__sub" style={{ margin: 0 }}>
                Scheduled to downgrade to Free on {fullDate(billing.renewsOn)}. You keep {current.name} until then.
              </div>
              <Button
                variant="secondary"
                loading={pending === billing.planId}
                disabled={pending !== null || !canManage}
                onClick={handleResume}
              >
                Keep my plan
              </Button>
            </div>
          )}
        </Card>

        {/* Plan picker */}
        <div>
          <div className="section-title">Change plan</div>
          {!canManage && (
            <div className="pay-error" style={{ marginBottom: '0.85rem' }}>
              Only an owner or admin can change the plan or payment method.
            </div>
          )}
          {!stripePromise && (
            <div className="pay-error" style={{ marginBottom: '0.85rem' }}>
              Card payments aren’t configured (no Stripe publishable key). Set STRIPE_PUBLISHABLE_KEY to enable plan changes.
            </div>
          )}
          <div className="plan-grid">
            {PLANS.filter((p) => !p.hidden).map((p) => {
              const isCurrent = p.id === billing.planId
              const isFree = p.id === 'free'
              const isEnterprise = p.price === null
              // Paid switches need Stripe; downgrade-to-free and contact-sales don't.
              const blocked = !stripePromise && !isEnterprise && !isFree
              const cls = [
                'plan-card',
                p.highlight && !isCurrent && 'plan-card--highlight',
                isCurrent && 'plan-card--current',
              ]
                .filter(Boolean)
                .join(' ')
              return (
                <div key={p.id} className={cls}>
                  {p.highlight && !isCurrent && <span className="plan-card__tag">Popular</span>}
                  <div className="plan-card__name">{p.name}</div>
                  <div className="plan-card__price">
                    {p.priceLabel}
                    {p.price ? <small> /mo</small> : null}
                  </div>
                  <div className="plan-card__tagline">{p.tagline}</div>
                  <ul className="plan-card__features">
                    {p.features.map((f) => (
                      <li key={f}>
                        <IconCheck size={13} /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="plan-card__cta"
                    variant={isCurrent ? 'secondary' : p.highlight ? 'primary' : 'secondary'}
                    block
                    disabled={isCurrent || blocked || !canManage || pending !== null || (isFree && scheduledDowngrade)}
                    loading={pending === p.id}
                    onClick={() => {
                      if (isEnterprise) {
                        window.location.assign('mailto:sales@tryvouchr.com?subject=Vouchr Enterprise plan')
                      } else if (isFree) {
                        handleCancel()
                      } else {
                        handleSwitch(p.id as PlanId, p.name)
                      }
                    }}
                  >
                    {isCurrent
                      ? 'Current plan'
                      : isEnterprise
                        ? 'Contact sales'
                        : isFree
                          ? scheduledDowngrade
                            ? 'Scheduled'
                            : hasSubscription
                              ? 'Downgrade'
                              : 'Current plan'
                          : 'Switch'}
                  </Button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Payment method */}
        <Card>
          <div className="row-between">
            <div>
              <div className="section-title" style={{ margin: 0 }}>Payment method</div>
              {billing.card.last4 ? (
                <>
                  <div style={{ marginTop: '0.4rem', fontWeight: 600 }}>
                    {billing.card.brand} ending {billing.card.last4}
                  </div>
                  <div className="setting-row__sub">Expires {billing.card.exp}</div>
                </>
              ) : (
                <div className="setting-row__sub" style={{ marginTop: '0.4rem' }}>
                  No card on file.
                </div>
              )}
            </div>
            <Button variant="secondary" disabled={!stripePromise || !canManage} onClick={handleUpdateCard}>
              {billing.card.last4 ? 'Update card' : 'Add card'}
            </Button>
          </div>
        </Card>

        {/* Invoices */}
        <Card>
          <div className="section-title">Invoices</div>
          {billing.invoices.length === 0 ? (
            <div className="setting-row__sub">No invoices yet.</div>
          ) : (
            billing.invoices.map((inv) => (
              <div key={inv.id} className="invoice-row">
                <span className="invoice-row__date">{fullDate(inv.date)}</span>
                <span className="invoice-row__desc">{inv.description}</span>
                <span className="mono" style={{ fontWeight: 700 }}>
                  {money(inv.amountCents)}
                </span>
                {inv.status === 'paid' ? (
                  <Badge color="var(--cred-live)" bg="var(--cred-live-bg)">Paid</Badge>
                ) : (
                  <Badge color="var(--c-warn)" bg="var(--c-warn-bg)">Due</Badge>
                )}
              </div>
            ))
          )}
        </Card>
      </div>

      {/* Card-entry modal — Elements is mounted here, keyed by the clientSecret
          the server returned, so the form only exists when there's an intent to
          confirm. */}
      <Modal
        open={!!flow}
        onClose={() => setFlow(null)}
        title={flow?.mode === 'setup' ? 'Update payment method' : `Subscribe to ${flow?.planName ?? ''}`}
      >
        {flow && stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: flow.clientSecret, appearance: { theme: 'night' } }}
          >
            <CardForm flow={flow} onDone={() => setFlow(null)} />
          </Elements>
        )}
      </Modal>
    </div>
  )
}
